import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiError';
import { singpassLoginError } from '../../utils/singpassLoginError';
import { AspenHealthLogo } from '../../components/AspenHealthLogo';

/**
 * Handles return from Singpass for doctor consult web (basename = CRA homepage / PUBLIC_URL).
 * Configure redirect URL in Singpass to: {portal}/singpass-callback/{state}
 */
function SingpassCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { state: stateParam } = useParams<{ state?: string }>();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const singpassFailure = singpassLoginError(searchParams.get('singpassError'));
      if (singpassFailure) {
        const resumeConsult = sessionStorage.getItem('drTele_externalSingpassResume');
        if (resumeConsult) {
          navigate(`/consult/${encodeURIComponent(resumeConsult)}?singpass=${singpassFailure}`, { replace: true });
          return;
        }
        navigate(`/login?singpass=${singpassFailure}`, { replace: true });
        return;
      }

      const state = stateParam?.trim() || searchParams.get('state')?.trim();
      if (!state) {
        setError('Missing Singpass state. Please try logging in again.');
        return;
      }

      try {
        await apiService.singpassDoctorLogin(state);
        if (cancelled) return;

        const resumeConsult = sessionStorage.getItem('drTele_externalSingpassResume');
        if (resumeConsult) {
          navigate(`/consult/${encodeURIComponent(resumeConsult)}`, { replace: true });
          return;
        }

        sessionStorage.setItem('drTele_postSingpassDoLogin', '1');
        navigate('/login', { replace: true });
      } catch (e: unknown) {
        if (cancelled) return;
        setError(getApiErrorMessage(e, 'Singpass login failed. Please try again.'));
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams, stateParam]);

  if (error) {
    return (
      <div className="login-container">
        <div className="login-box">
          <AspenHealthLogo variant="login" showPortalLabel />
          <p className="login-subtitle">Singpass</p>
          <div style={{ color: '#f87171', marginBottom: 16, fontSize: 14 }}>{error}</div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              const resumeConsult = sessionStorage.getItem('drTele_externalSingpassResume');
              if (resumeConsult) {
                navigate(`/consult/${encodeURIComponent(resumeConsult)}`, { replace: true });
                return;
              }
              navigate('/login', { replace: true });
            }}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <AspenHealthLogo variant="login" showPortalLabel />
        <p className="login-subtitle">Completing Singpass sign-in…</p>
      </div>
    </div>
  );
}

export default SingpassCallbackPage;
